import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'zh', label: '简体中文' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
  { code: 'pt', label: 'Português' },
  { code: 'es', label: 'Español' },
  { code: 'nl', label: 'Nederlands' },
];

interface LanguageSwitcherProps {
  scrolled?: boolean;
}

export default function LanguageSwitcher({ scrolled = false }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem('lang', code);
    setOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap
          ${scrolled
            ? 'hover:bg-[#f7f5f2] text-[#444]'
            : 'hover:bg-white/10 text-white'
          }`}
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-global-line text-sm"></i>
        </div>
        <span className="text-xs font-medium">{current.label}</span>
        <div className="w-3 h-3 flex items-center justify-center">
          <i className={`ri-arrow-down-s-line text-xs transition-transform ${open ? 'rotate-180' : ''}`}></i>
        </div>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-[#ede9e3] py-1 min-w-[150px] z-50">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors cursor-pointer
                ${lang.code === i18n.language
                  ? 'bg-[#fdf6f2] text-[#C8714A] font-medium'
                  : 'text-[#333] hover:bg-[#faf9f7]'
                }`}
            >
              <span>{lang.label}</span>
              {lang.code === i18n.language && (
                <div className="ml-auto w-4 h-4 flex items-center justify-center text-[#C8714A]">
                  <i className="ri-check-line text-xs"></i>
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}